/**
 * A round that has stalled: the stock is empty, the pile too thin to rebuild it, and
 * nothing has been laid for two laps. The players choose — end the round, or shuffle in
 * another pack and carry on. The game never decides for them. Ben's call, 1 October 2026.
 * scope.md §3.8.
 */

import type { DeadRoundChoice, PlayerView } from '@garbage/shared';
import { useT } from '../i18n.js';

export function DeadRoundPanel({
  view, onVote,
}: {
  view: PlayerView;
  onVote: (choice: DeadRoundChoice) => void;
}) {
  const t = useT();
  const dead = view.deadRound;
  if (!dead) return null;

  const mine = dead.votes[view.you];
  const label = (choice: DeadRoundChoice) => (choice === 'end' ? t('dead.end') : t('dead.addPack'));
  // Bots and anybody away never hold this up, so only people still to answer are named.
  const waiting = view.players
    .filter((p) => !p.bot && p.connected && dead.votes[p.id] === undefined && p.id !== view.you)
    .map((p) => p.name);

  return (
    <section
      data-testid="dead-round"
      aria-live="polite"
      className="mx-3 mb-1 rounded-2xl border border-violet-300/40 bg-violet-400/10 px-3 py-2 text-center"
    >
      <p className="font-bold text-violet-100">{t('dead.title')}</p>
      <p className="text-xs text-bone/70">{t('dead.why')}</p>
      <p className="pt-1 text-xs text-bone/70">
        {dead.choices.includes('addPack') ? t('dead.rule') : t('dead.endOnly')}
      </p>

      {mine === undefined ? (
        <div className="flex justify-center gap-2 pt-2">
          {dead.choices.map((choice) => (
            <button
              key={choice}
              type="button"
              data-testid={`dead-${choice}`}
              onClick={() => onVote(choice)}
              className={[
                'rounded-2xl px-4 py-2 font-black active:scale-95',
                choice === 'end' ? 'bg-white/15' : 'bg-sun text-ink',
              ].join(' ')}
            >
              {label(choice)}
            </button>
          ))}
        </div>
      ) : (
        <p className="pt-1 text-sm font-bold" data-testid="dead-voted">{t('dead.voted', { choice: label(mine) })}</p>
      )}
      {waiting.length > 0 && (
        <p className="pt-1 text-xs text-bone/60">{t('dead.waiting', { names: waiting.join(', ') })}</p>
      )}
    </section>
  );
}
